import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { calculateQuestionScores, calculateImpactScores } from '../../utils/calculations';

const { FiArrowLeft, FiBookOpen, FiTrendingUp, FiTarget } = FiIcons;

const RecommendationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recommendation, setRecommendation] = useState(null);
  const [impactScore, setImpactScore] = useState(0);
  const [affectedQuestions, setAffectedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecommendation = async () => {
      try {
        setLoading(true);
        // 1. Fetch Recommendation and Metadata (Questions, Relations)
        const [recRes, questionsRes, recRelationsRes] = await Promise.all([
          apiService.getRecommendationById(id),
          apiService.getQuestions({ limit: 1000 }),
          apiService.getQuestionRecommendationRelations({ limit: 1000 })
        ]);
        
        if (recRes && recRes.data) {
          const recData = recRes.data;
          setRecommendation(recData);
          
          let allQuestions = questionsRes.data || [];
          const allRecRelations = recRelationsRes.data || [];
          let questionsWithScores = allQuestions.map(q => ({ ...q, averageScore: 0, evaluationCount: 0 }));

          // 2. Calculate Impact Score & Question Scores if user has team
          if (user?.teamId) {
             const teamMembersRes = await apiService.getUserTeamRelations({ team_id: user.teamId, limit: 1000 });
             const teamMembers = teamMembersRes.data || [];
             
             const teamMemberIds = teamMembers.map(m => m.user_id);
             if (user.id && !teamMemberIds.includes(parseInt(user.id))) {
               teamMemberIds.push(parseInt(user.id));
             }

             const evaluationResponses = await Promise.all(
               teamMemberIds.map(uid => apiService.getEvaluations({ user_id: uid, limit: 1000 }))
             );
             const allEvaluations = evaluationResponses.flatMap(res => res.data || []);

             questionsWithScores = calculateQuestionScores(allQuestions, allEvaluations);
             const impactMap = calculateImpactScores([recData], questionsWithScores, allRecRelations);
             
             if (impactMap[recData.id]) {
               setImpactScore(impactMap[recData.id].score);
             }
          }

          // 3. Determine Affected Questions
          // Use loose equality for IDs to ensure matching between string/number types
          const linkedRelations = allRecRelations.filter(r => 
            r.recommendation_id == id || r.recommendation == id
          );
          const linkedQuestionIds = linkedRelations.map(r => r.question_id);
          
          const affected = questionsWithScores.filter(q => 
             linkedQuestionIds.some(lqId => lqId == q.id)
          );
          
          setAffectedQuestions(affected);

        } else {
          setError('Empfehlung nicht gefunden');
        }
      } catch (err) {
        console.error('Error fetching recommendation:', err);
        setError('Fehler beim Laden der Empfehlung');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRecommendation();
    }
  }, [id, user]);

  const getScoreColor = (score) => {
    if (score >= 6) return 'bg-green-50 text-green-700 border-green-200';
    if (score >= 4) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (error || !recommendation) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 mb-4">{error || 'Empfehlung nicht gefunden'}</p>
        <button
          onClick={() => navigate('/handbook')}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark transition-colors"
        >
          Zurück zum Handbuch
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 w-full"> 
      <button
        onClick={() => navigate('/handbook')}
        className="flex items-center text-gray-600 hover:text-brand-primary mb-6 transition-colors group"
      >
        <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        Zurück zum Handbuch
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-8 border-b border-gray-100 bg-gray-50/50">
          <div className="flex justify-between items-start">
             <div className="flex items-center gap-3 text-brand-secondary mb-3">
               <SafeIcon icon={FiBookOpen} className="w-5 h-5" />
               <span className="text-sm font-bold uppercase tracking-wider">Handlungsempfehlung</span>
             </div>
             
             {/* Impact Score - Always Visible */}
             <div className="flex items-center gap-2 px-3 py-1 bg-brand-primary/10 text-brand-secondary rounded-full border border-brand-primary/20">
               <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
               <span className="text-sm font-bold">Impact Score: {impactScore.toFixed(1)}</span>
             </div>
          </div>
          
          <h1 className="text-3xl font-bold text-brand-dark">{recommendation.title}</h1>
          {recommendation.short_description && (
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              {recommendation.short_description}
            </p>
          )}
        </div>

        <div className="p-8">
          <div 
            className="prose prose-lg max-w-none text-gray-700 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-8 [&>h2]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>li]:mb-2"
            dangerouslySetInnerHTML={{ __html: recommendation.description }}
          />
        </div>

        {/* Affected Questions Section */}
        {affectedQuestions.length > 0 && (
          <div className="p-8 border-t border-gray-100 bg-gray-50/30">
            <h3 className="text-lg font-bold text-brand-dark mb-4 flex items-center">
              <SafeIcon icon={FiTarget} className="mr-2 text-brand-secondary" />
              Einfluss auf folgende Fragen
            </h3>
            <div className="grid gap-3">
              {affectedQuestions.map(q => (
                 <div key={q.id} className="bg-white p-4 rounded-lg border border-gray-200 flex justify-between items-center shadow-sm">
                    <span className="font-medium text-gray-800">{q.text}</span>
                    {q.evaluationCount > 0 && (
                       <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${getScoreColor(q.averageScore)}`}>
                         <div className="text-xs font-bold uppercase tracking-wider opacity-70">Ø Score</div>
                         <div className="font-bold">{q.averageScore.toFixed(1)}</div>
                       </div>
                    )}
                 </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationDetail;